import {useEffect, useState} from "react";
import {BackendApi} from "../../api/api.ts";
import {ClipboardIcon} from "@heroicons/react/16/solid";
import RelativeLoadingOverlayFrame from "../RelativeLoadingOverlayFrame.tsx";
import CollapsibleTable, {DataSource} from "./CollapsibleTable.tsx";
import {useNotifications} from "../../lib/NotificationContext.tsx";
import {CASHFLOW_LABELS, CashflowLabelEnum} from "../../types/enums.ts";
import BankAccountSelector from "./BankAccountSelector.tsx";
import {BankAccountData, CustomerBankAccount} from "../../api/customerBankAccounts/CustomerBankAccount.ts";
import type {LegalEntityType} from '../../generated/enums.ts';

enum CashflowDataKeys {
    CASHFLOW = 'cashflow',
    CASHFLOW_OPR = 'cashflow_opr',
    CASHFLOW_NON_OPR = 'cashflow_non_opr',
    INT_PERSONNEL_COSTS = 'int_personnel_costs',
    INT_RENT_AND_FACILITIES = 'int_rent_and_facilities',
    INT_OFFICE_COSTS = 'int_office_costs',
    INT_SALES_AND_MARKETING = 'int_sales_and_marketing',
    INT_SOFTWARE = 'int_software',
    INT_PROFESSIONAL_SERVICES = 'int_professional_services',
    INT_CAR_AND_EQUIPMENT = 'int_car_and_equipment',
    INT_BANKING_AND_INSURANCE = 'int_banking_and_insurance',
    INT_EXPENSE_TAXES = 'int_expense_taxes',
    INT_OTHER_EXPENSES = 'int_other_expenses',
    INT_UNCATEGORIZED_EXPENSE = 'int_uncategorized_expense',
    BALANCE_START = 'balance_start',
    BALANCE_END = 'balance_end'
}

const expenseCategoryMapping = {
    [CashflowDataKeys.INT_PERSONNEL_COSTS]: 'Personeelskosten',
    [CashflowDataKeys.INT_RENT_AND_FACILITIES]: 'Huur & Faciliteiten',
    [CashflowDataKeys.INT_OFFICE_COSTS]: 'Kantoorkosten',
    [CashflowDataKeys.INT_SALES_AND_MARKETING]: 'Verkoop & Marketing',
    [CashflowDataKeys.INT_SOFTWARE]: 'Software',
    [CashflowDataKeys.INT_PROFESSIONAL_SERVICES]: 'Zakelijke dienstverlening',
    [CashflowDataKeys.INT_CAR_AND_EQUIPMENT]: 'Auto & Apparatuur',
    [CashflowDataKeys.INT_BANKING_AND_INSURANCE]: 'Bank & Verzekeringen',
    [CashflowDataKeys.INT_EXPENSE_TAXES]: 'Belastingen',
    [CashflowDataKeys.INT_OTHER_EXPENSES]: 'Overige kosten',
    [CashflowDataKeys.INT_UNCATEGORIZED_EXPENSE]: 'Ongecategoriseerde kosten'
};

export interface Business {
    id: string
    name: string
    legal_entity_type: LegalEntityType
    registration_number: string
    fte_amount: number
    industry: string
    description: string
    managed_by: string | null
    transaction_history_cutoff_date: {
        cutoff_date: string | null
    }
}

interface CashflowTableProps {
    business: Business
    api: BackendApi
    showBankAccountSelector?: boolean
    isPartnerView?: boolean
}

export default function CashflowStatement({ business, api, showBankAccountSelector = true, isPartnerView = false }: CashflowTableProps) {
    const [isLoadingMain, setIsLoadingMain] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [allBankAccounts, setAllBankAccounts] = useState<CustomerBankAccount[]>([]);
    const [selectedBankAccount, setSelectedBankAccount] = useState<CustomerBankAccount>();
    const [cashflowData, setCashflowData] = useState<Array<any>>([]);
    const [showCopyFeedback, setShowCopyFeedback] = useState(false);
    const {dispatch: dispatchNotification} = useNotifications();

    async function load() {
        try {
            if (showBankAccountSelector) {
                const response = isPartnerView
                    ? await api.Partner__entrepreneur__getBankAccounts(business.id)
                    : await api.Admin__business__bankAccounts(business.id)

                const accounts = response.map(account => CustomerBankAccount.fromApiResponse(account as BankAccountData))
                setAllBankAccounts(accounts)
                const primaryAccount = accounts[0]
                setSelectedBankAccount(primaryAccount)

                if (primaryAccount?.id) {
                    const cashflowData = await loadCashflowStatement(primaryAccount.id)
                    setCashflowData(cashflowData)
                } else {
                    setCashflowData([])
                }
            } else {
                const cashflowData = await loadCashflowStatement()
                setCashflowData(cashflowData)
            }
        } catch (error) {
            console.error('Failed to load cashflow data:', error);
            setCashflowData([])
            dispatchNotification({
                title: 'Kon cashflow gegevens niet laden',
                description: 'Kan bankrekeningen of cashflow informatie niet ophalen. Probeer het opnieuw.',
                type: 'error'
            })
        } finally {
            setIsLoadingMain(false);
        }
    }

    async function loadCashflowStatement(bankAccountId?: string) {
        setIsLoading(true);
        try {
            const response = await api.Business__cashflowStatement(business.id, bankAccountId)
            setIsLoading(false);
            return response
        } catch (error) {
            console.error('Failed to load cashflow statement:', error);
            setIsLoading(false)
            return []
        }
    }

    useEffect(() => {
        if (business?.id) {
            load();
        }
    }, [business?.id]);

    function getCashflowDataByKey(cashflowData: any[], key: CashflowDataKeys): Record<string, any> {
        const result: Record<string, any> = {};
        cashflowData.forEach(month => {
            result[month.month] = month[key];
        });
        return result;
    }

    function getAllRelations(cashflowData) {
        const allRelations = new Set();
        cashflowData.forEach(month => {
            (month.customers || []).forEach(c => allRelations.add(c.customer));
            (month.suppliers || []).forEach(s => allRelations.add(s.supplier));
        });
        return Array.from(allRelations).sort();
    }

    function getAllExpenseTypeRelations(cashflowData, expenseCategory) {
        const allRelations = new Set();
        cashflowData.forEach(month => {
            (month[expenseCategory] || []).forEach(s => allRelations.add(s.supplier));
        });
        return Array.from(allRelations).sort();
    }

    function formatNumber(number, decimals, thousands_separator, decimal_separator) {
        var t = [];
        var original_number = number;
        if (typeof decimals == 'undefined') decimals = 0;
        if (typeof thousands_separator == 'undefined') thousands_separator = '.';
        if (typeof decimal_separator == 'undefined') decimal_separator = ',';

        if (original_number < 0) {
            number = number.toString().substring(1);
        }

        number = Number(number).toFixed(decimals).split('.');

        for (var iLen = number[0].length, i = iLen ? iLen % 3 || 3 : 0, j = 0; i <= iLen; i += 3) {
            t.push(number[0].substring(j, i));
            j = i;
        }

        if (original_number < 0) {
            return '-' + t.join(thousands_separator) + (number[1] ? decimal_separator + number[1] : '');
        }

        return t.join(thousands_separator) + (number[1] ? decimal_separator + number[1] : '');
    }

    const formatRelationName = (name: string): string => {
        if (!name) return '';
        const words = name.split(' ');
        const formatted = words.map(word => {
            if (word.toLowerCase().startsWith('b.v.') || word.toLowerCase() === 'bv') {
                return 'B.V.';
            }
            if (word.toLowerCase().startsWith('n.v.') || word.toLowerCase() === 'nv') {
                return 'N.V.';
            }
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }).join(' ');
        return formatted;
    };

    const normalizeName = (name: string) =>
        name.trim().replace(/[^\x20-\x7E]/g, '').replace(/\s+/g, ' ')
            .replace(/\./g, '').toLowerCase();

    const getExpenseCashFlow = (monthData, relationName, expenseCategory) => {
        if (!relationName) return 0;
        const normalizedRelation = normalizeName(relationName);
        const categoryData = monthData[expenseCategory] || [];
        const expense = categoryData.find(e =>
            (e.supplier && normalizeName(e.supplier) === normalizedRelation) ||
            (e.customer && normalizeName(e.customer) === normalizedRelation)
        );
        return expense?.cash_flow || 0;
    }

    const getNonOperationalCashFlow = (monthData, relationName) => {
        const normalizedRelation = normalizeName(relationName);
        const customer = (monthData.customers_non_opr || []).find(c =>
            normalizeName(c.customer) === normalizedRelation
        );
        const supplier = (monthData.suppliers_non_opr || []).find(s =>
            normalizeName(s.supplier) === normalizedRelation
        );
        return (customer?.cash_flow || 0) + (supplier?.cash_flow || 0);
    }

    function getAllNonOperationalRelations(cashflowData) {
        const allRelations = new Set();
        cashflowData.forEach(month => {
            (month.customers_non_opr || []).forEach(c => allRelations.add(c.customer));
            (month.suppliers_non_opr || []).forEach(s => allRelations.add(s.supplier));
        });
        return Array.from(allRelations).sort();
    }

    const copyTableToClipboard = async () => {
        const table = document.querySelector('.finance-table');
        if (!table) {
            console.error('Table not found');
            return;
        }

        try {
            const tableClone = table.cloneNode(true) as HTMLTableElement;
            
            const buttons = tableClone.querySelectorAll('button');
            buttons.forEach(button => button.remove());
            
            const svgElements = tableClone.querySelectorAll('svg');
            svgElements.forEach(svg => svg.remove());
            
            const tags = tableClone.querySelectorAll('.copy-exclude');
            tags.forEach(tag => tag.remove());

            const tableText = convertTableToText(tableClone);
            
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(tableText);
            } else {
                const textArea = document.createElement('textarea');
                textArea.value = tableText;
                textArea.style.position = 'fixed';
                textArea.style.left = '-9999px';
                textArea.style.top = '-9999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }
        } catch (error) {
            console.error('Failed to copy table:', error);
        }
    };

    const convertTableToText = (table: HTMLTableElement): string => {
        const rows: string[] = [];
        
        const headerRow = table.querySelector('thead tr');
        if (headerRow) {
            const headerCells = Array.from(headerRow.querySelectorAll('th'));
            const headerText = headerCells.map(cell => cell.textContent?.trim() || '').join('\t');
            rows.push(headerText);
        }
        
        const bodyRows = table.querySelectorAll('tbody tr');
        bodyRows.forEach(row => {
            const cells = Array.from(row.querySelectorAll('td'));
            const rowText = cells.map(cell => {
                let text = cell.textContent?.trim() || '';
                text = text.replace(/\s+/g, ' ');
                return text;
            }).join('\t');
            rows.push(rowText);
        });
        
        return rows.join('\n');
    };

    const createSeparator = (key: string, label: string, style: "H1" | "H2" | "H3" | "H4", isCollapsible = false, showTag = false): DataSource => ({
        key,
        data: { label },
        isSeparator: true,
        separatorStyle: style,
        isCollapsible,
        defaultCollapsed: isCollapsible,
        showTag,
    });

    const createCashFlowData = (relationName: string, getCashFlowFn: (month: any, name: string) => number): Record<string, any> => {
        return cashflowData.reduce((acc, month) => {
            acc[month.month] = getCashFlowFn(month, relationName);
            return acc;
        }, {} as Record<string, any>);
    };

    const createRelationRow = (key: string, relationName: string, parentKey: string, level: number, getCashFlowFn: (month: any, name: string) => number): DataSource => ({
        key,
        data: {
            label: formatRelationName(relationName),
            ...createCashFlowData(relationName, getCashFlowFn)
        },
        level,
        parentKey
    });

    const addCashBalanceSection = (data: DataSource[]): void => {
        data.push(
            {
                key: CashflowDataKeys.BALANCE_START,
                data: {
                    label: CASHFLOW_LABELS[CashflowLabelEnum.CASH_START_OF_PERIOD],
                    ...getCashflowDataByKey(cashflowData, CashflowDataKeys.BALANCE_START),
                },
                level: 0,
            },
            {
                key: CashflowDataKeys.BALANCE_END,
                data: {
                    label: CASHFLOW_LABELS[CashflowLabelEnum.CASH_END_OF_PERIOD],
                    ...getCashflowDataByKey(cashflowData, CashflowDataKeys.BALANCE_END),
                },
                level: 0,
            }
        );
    };

    const addOperationsSection = (data: DataSource[]): void => {
        data.push(createSeparator('h1-operations', CASHFLOW_LABELS[CashflowLabelEnum.OPERATIONS], 'H1'));
        data.push(createSeparator('h2-cash-in-sources', CASHFLOW_LABELS[CashflowLabelEnum.CASH_IN_SOURCES], 'H2', true, true));

        const cashInRelations = getAllRelations(cashflowData)
            .filter(name => cashflowData.some(month => {
                const customer = (month.customers || []).find(c => c.customer === name);
                return customer && customer.cash_flow > 0;
            }));

        cashInRelations.forEach((name: string) => {
            data.push(createRelationRow(
                `cash-in-${name}`,
                name,
                'h2-cash-in-sources',
                4,
                (month, relationName) => {
                    const customer = (month.customers || []).find(c => c.customer === relationName);
                    return customer ? customer.cash_flow : 0;
                }
            ));
        });

        data.push(createSeparator('h2-cash-out-sources', CASHFLOW_LABELS[CashflowLabelEnum.CASH_OUT_SOURCES], 'H2', true, true));

        Object.entries(expenseCategoryMapping).forEach(([cashflowKey, label]) => {
            const totalCashflow = getCashflowDataByKey(cashflowData, cashflowKey as CashflowDataKeys);
            
            data.push({
                key: cashflowKey,
                data: { label },
                isCollapsible: true,
                defaultCollapsed: true,
                showTag: true,
                parentKey: "h2-cash-out-sources",
                aggregatedData: totalCashflow,
                level: 2
            });

            const expenseCategory = cashflowKey.replace('int_', '');
            const relations = getAllExpenseTypeRelations(cashflowData, expenseCategory);

            relations.forEach((relationName: string) => {
                data.push(createRelationRow(
                    `${cashflowKey}-${relationName}`,
                    relationName,
                    cashflowKey,
                    4,
                    (month, relationName) => getExpenseCashFlow(month, relationName, expenseCategory)
                ));
            });

            data.push({
                key: `${cashflowKey}-total`,
                data: {
                    label: CASHFLOW_LABELS[CashflowLabelEnum.TOTAL],
                    ...totalCashflow
                },
                level: 3,
                parentKey: cashflowKey,
                ignoreFromTagcount: true,
            });
        });

        data.push(
            createSeparator('h3-operations-cash-seperator', '', 'H3'),
            {
                key: 'h3-operations-cash',
                data: {
                    label: CASHFLOW_LABELS[CashflowLabelEnum.OPERATIONS_CASH],
                    ...getCashflowDataByKey(cashflowData, CashflowDataKeys.CASHFLOW_OPR),
                },
                level: 2,
                separatorStyle: 'H3',
            }
        );
    };

    const addNonOperationalSection = (data: DataSource[]): void => {
        data.push(createSeparator('h1-non-operational', CASHFLOW_LABELS[CashflowLabelEnum.NON_OPERATIONAL], 'H1'));
        data.push(createSeparator('h2-non-operational-cash-in-sources', CASHFLOW_LABELS[CashflowLabelEnum.CASH_IN_SOURCES], 'H2', true, true));

        const nonOprCashInRelations = getAllNonOperationalRelations(cashflowData)
            .filter(name => cashflowData.some(month => getNonOperationalCashFlow(month, name) > 0));

        nonOprCashInRelations.forEach((name: string) => {
            data.push(createRelationRow(
                `non-opr-cash-in-${name}`,
                name,
                'h2-non-operational-cash-in-sources',
                4,
                (month, relationName) => {
                    const cashFlow = getNonOperationalCashFlow(month, relationName);
                    return cashFlow > 0 ? cashFlow : 0;
                }
            ));
        });

        data.push(createSeparator('h2-non-operational-cash-out-sources', CASHFLOW_LABELS[CashflowLabelEnum.CASH_OUT_SOURCES], 'H2', true, true));
        const nonOprCashOutRelations = getAllNonOperationalRelations(cashflowData)
            .filter(name => cashflowData.some(month => getNonOperationalCashFlow(month, name) < 0));

        nonOprCashOutRelations.forEach((name: string) => {
            data.push(createRelationRow(
                `non-opr-cash-out-${name}`,
                name,
                'h2-non-operational-cash-out-sources',
                4,
                (month, relationName) => {
                    const cashFlow = getNonOperationalCashFlow(month, relationName);
                    return cashFlow < 0 ? cashFlow : 0;
                }
            ));
        });
        data.push(
            createSeparator('h3-non-operations-cash-seperator', '', 'H3'),
            {
                key: 'h3-non-operations-cash',
                data: {
                    label: CASHFLOW_LABELS[CashflowLabelEnum.NON_OPERATIONAL_CASH],
                    ...getCashflowDataByKey(cashflowData, CashflowDataKeys.CASHFLOW_NON_OPR),
                },
                level: 2,
                separatorStyle: 'H3',
            }
        );
    };

    const addNetCashflowSection = (data: DataSource[]): void => {
        data.push(
            createSeparator('h1-net-cashflow', CASHFLOW_LABELS[CashflowLabelEnum.NET_CASHFLOW], 'H1'),
            {
                key: 'h3-net-cashflow',
                data: {
                    label: '',
                    ...getCashflowDataByKey(cashflowData, CashflowDataKeys.CASHFLOW),
                },
                level: 2,
                separatorStyle: 'H3',
            }
        );
    };

    const getDataSource = (): DataSource[] => {
        const data: DataSource[] = [];

        addCashBalanceSection(data);
        addOperationsSection(data);
        addNonOperationalSection(data);
        addNetCashflowSection(data);

        return data;
    };

    const generateMonthColumns = () => {
        if (!cashflowData || cashflowData.length === 0) {
            return [];
        }
        const monthKeys = cashflowData.map(month => month.month);
        const columns = [
            {
                key: 'label',
                title: '',
                dataIndex: 'label',
                fixed: 'left' as const,
                render: (value: string) => (
                    <span className="text-sm font-medium text-gray-900 max-w-md truncate block">
                        {value}
                    </span>
                ),
            }
        ];

        const monthColumns = monthKeys.map(monthKey => {
            return {
                key: monthKey,
                title: monthKey,
                dataIndex: monthKey,
                width: 100,
                textAlign: 'right' as const,
                render: (value: number) => {
                    if (value === 0 || value === null || value === undefined) {
                        return (
                            <span className="text-sm text-gray-400">
                                0
                            </span>
                        );
                    } else if (value < 0) {
                        return (
                            <span className="text-sm text-red-600">
                                {formatNumber(value, 0, '.', ',')}
                            </span>
                        );
                    } else {
                        return (
                            <span className="text-sm text-gray-900">
                                {formatNumber(value, 0, '.', ',')}
                            </span>
                        );
                    }
                },
            };
        });

        return [...columns, ...monthColumns];
    };

    return (
        <div>
            <div className="flex justify-between items-center my-8">
                {showBankAccountSelector && (
                    <BankAccountSelector
                        label="Bankrekening"
                        bankAccounts={allBankAccounts}
                        selectedAccountId={selectedBankAccount?.id}
                        onSelect={async (account) => {
                            if (!account) return
                            try {
                                setSelectedBankAccount(account)
                                const cashflowData = await loadCashflowStatement(account.id)
                                setCashflowData(cashflowData)
                            } catch (error) {
                                console.error('Failed to load cashflow data for selected account:', error);
                                setCashflowData([])
                                dispatchNotification({
                                    title: 'Kon bankrekening niet wisselen',
                                    description: 'Kan cashflow gegevens voor de geselecteerde rekening niet laden. Probeer het opnieuw.',
                                    type: 'error'
                                })
                            }
                        }}
                    />
                )}
                
                <button
                    onClick={async () => {
                        await copyTableToClipboard();
                        setShowCopyFeedback(true);
                        setTimeout(() => setShowCopyFeedback(false), 2000);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm"
                >
                    <ClipboardIcon className="h-4 w-4" />
                    {showCopyFeedback ? 'Gekopieerd!' : 'Tabel kopiëren'}
                </button>
            </div>

            <RelativeLoadingOverlayFrame isLoading={isLoadingMain || isLoading}>
                {!!cashflowData && cashflowData.length > 0 ? (
                    <CollapsibleTable dataSource={getDataSource()} columns={generateMonthColumns()} width="100%"  className="finance-table" />
                ) : !isLoadingMain && !isLoading && showBankAccountSelector && allBankAccounts.length === 0 ? (
                    <div className="text-center py-8">
                        <p className="text-gray-500 text-lg">Geen bankrekeningen gevonden voor dit bedrijf.</p>
                        <p className="text-gray-400 text-sm mt-2">Voeg bankrekeningen toe om het cashflow overzicht te bekijken.</p>
                    </div>
                ) : null}
            </RelativeLoadingOverlayFrame>
        </div>
    )
}
